import { useState } from "react";
import { toast } from "sonner";
import { ShieldAlert, UserX, CreditCard, XOctagon, Loader2, ChevronDown, CheckCircle2 } from "lucide-react";
import api, { errMsg } from "@/lib/api";

/**
 * "Recover Revenue" dropdown — only rendered when `booking.channel === "Booking.com"`.
 * The three actions map to Channex's BDC virtual-card reporting endpoints:
 *   - Report No-Show          → /bookings/:id/report-no-show
 *   - Report Invalid Card     → /bookings/:id/report-invalid-card
 *   - Cancel Due Invalid Card → /bookings/:id/cancel-due-invalid-card
 *
 * Each action requires a confirmation modal (destructive · irreversible on
 * Booking.com's side). Once reported, the button flips to a green
 * "Reported on {date}" pill so hosts don't double-file.
 */

const ACTIONS = [
  {
    key: "no_show", label: "Report No-Show", endpoint: "report-no-show",
    icon: UserX,
    danger_copy: "Booking.com will be notified that the guest did NOT arrive. Must be done within 48 hours of check-in. This cancels the guest's ability to leave a review and releases you from paying commission.",
    success: "Booking.com notified — no-show reported.",
  },
  {
    key: "invalid_card", label: "Report Invalid Card", endpoint: "report-invalid-card",
    icon: CreditCard,
    danger_copy: "Flag the guest's virtual credit card as declined. Booking.com will contact the guest for a new card. Only use if you attempted to charge and the card genuinely failed.",
    success: "Booking.com notified — they will contact the guest for a new card.",
  },
  {
    key: "cancel_due_invalid_card", label: "Cancel — Invalid Card", endpoint: "cancel-due-invalid-card",
    icon: XOctagon,
    danger_copy: "Cancel this booking on Booking.com because the guest's card was invalid AND you were unable to reach them. This is IRREVERSIBLE — the booking will be cancelled without commission charge.",
    success: "Booking cancelled on Booking.com — commission waived.",
  },
];

export default function BookingRecoveryMenu({ booking, onDone }) {
  const [open, setOpen] = useState(false);
  const [confirming, setConfirming] = useState(null); // action key being confirmed
  const [busy, setBusy] = useState(false);

  if ((booking?.channel || "").trim() !== "Booking.com") return null;
  const recovery = booking.recovery || {};

  const run = async () => {
    const act = ACTIONS.find((a) => a.key === confirming);
    if (!act) return;
    setBusy(true);
    try {
      await api.post(`/pms/bookings/${booking.id}/${act.endpoint}`);
      toast.success(act.success);
      setConfirming(null); setOpen(false);
      onDone?.();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  return (
    <div className="relative inline-block">
      <button data-testid="recovery-menu-trigger" onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100">
        <ShieldAlert size={12} /> Recover revenue <ChevronDown size={11} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div data-testid="recovery-menu-dropdown"
            className="absolute right-0 mt-2 w-72 bg-white rounded-xl shadow-2xl border border-slate-200 z-50 overflow-hidden">
            <p className="px-4 py-2.5 text-[10px] font-bold uppercase tracking-wider text-slate-500 border-b border-slate-100 bg-slate-50">
              Booking.com only · 48h SLA from check-in
            </p>
            {ACTIONS.map((a) => {
              const done = recovery[a.key]?.ok;
              const at = recovery[a.key]?.reported_at;
              const Icon = a.icon;
              return (
                <button key={a.key}
                  data-testid={`recovery-action-${a.key}`}
                  onClick={() => !done && setConfirming(a.key)}
                  disabled={!!done}
                  className={`w-full text-left px-4 py-2.5 flex items-start gap-2.5 border-b border-slate-100 last:border-0 ${
                    done ? "bg-emerald-50/50 cursor-default" : "hover:bg-slate-50"
                  }`}>
                  <Icon size={14} className={`mt-0.5 shrink-0 ${done ? "text-emerald-600" : "text-slate-500"}`} />
                  <div className="min-w-0">
                    <p className={`text-xs font-semibold ${done ? "text-emerald-700" : "text-slate-900"}`}>
                      {done && <CheckCircle2 size={11} className="inline mr-1" />}
                      {a.label}
                    </p>
                    {done && at ? (
                      <p className="text-[10px] text-emerald-600 mt-0.5">Reported {new Date(at).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}</p>
                    ) : (
                      <p className="text-[10px] text-slate-400 mt-0.5 leading-relaxed">Sends a report to Booking.com via Channex.</p>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </>
      )}

      {confirming && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4"
          onClick={() => !busy && setConfirming(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-3">
              <span className="h-10 w-10 rounded-xl bg-amber-50 border border-amber-200 text-amber-600 flex items-center justify-center shrink-0"><ShieldAlert size={19} /></span>
              <div className="min-w-0">
                <h3 className="font-display font-bold text-slate-900">{ACTIONS.find((a) => a.key === confirming).label}?</h3>
                <p className="text-xs text-slate-500 mt-1 leading-relaxed">{ACTIONS.find((a) => a.key === confirming).danger_copy}</p>
                <p className="text-[11px] text-slate-400 mt-2 font-mono">Booking: {booking.guest_name || booking.id} · Channel: Booking.com</p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 mt-5">
              <button data-testid="recovery-confirm-cancel" onClick={() => setConfirming(null)} disabled={busy}
                className="text-xs font-semibold px-4 py-2 rounded-full border border-slate-300 text-slate-500 hover:border-slate-500">
                Cancel
              </button>
              <button data-testid="recovery-confirm-submit" onClick={run} disabled={busy}
                className="text-xs font-semibold px-4 py-2 rounded-full bg-amber-600 text-white hover:bg-amber-700 flex items-center gap-2">
                {busy ? <Loader2 className="animate-spin" size={13} /> : <ShieldAlert size={13} />} Yes, report to Booking.com
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
